import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { DocumentsService } from "../../api/documents";
import { getAllPatients } from "../../api/patient";

const DocumentsForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const [patients, setPatients] = useState<
    { id: string; name: string; treatmentId?: string }[]
  >([]);
  const [patientsLoading, setPatientsLoading] = useState(false);

  const [selectedPatientId, setSelectedPatientId] = useState<string>("");
  const [beforeImages, setBeforeImages] = useState<File[]>([]);
  const [afterImages, setAfterImages] = useState<File[]>([]);
  const [medicalPdfs, setMedicalPdfs] = useState<File[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const res = await getAllPatients();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : []).map((p: any) => ({
          id: p.id || p._id || p.patientId || p.value || "",
          name:
            p.name ||
            `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
            p.label ||
            "",
          treatmentId: p.treatment_id || p.treatmentId || "",
        }));
        setPatients(normalized.filter((p) => p.id && p.name));
      } catch {
        setPatients([]);
      } finally {
        setPatientsLoading(false);
      }
    };

    fetchPatients();
  }, []);

  const handleFilesChange =
    (setter: React.Dispatch<React.SetStateAction<File[]>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (!files.length) return;
      if (files.length > 1) {
        toast.warn("You can upload a maximum of 1 file in this section.");
      }
      setter([files[0]]);
      e.currentTarget.value = "";
    };

  const handleRemoveFile =
    (setter: React.Dispatch<React.SetStateAction<File[]>>) =>
    (index: number) => {
      setter((prev) => prev.filter((_, i) => i !== index));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPatientId) {
      toast.error("Please select a patient.");
      return;
    }

    const selectedPatient = patients.find((p) => p.id === selectedPatientId);
    const treatmentId = selectedPatient?.treatmentId;

    if (!treatmentId) {
      toast.error("Please create treatment first for this patient.");
      return;
    }

    try {
      setLoading(true);
      if (id) {
        await DocumentsService.update(
          id,
          { treatmentId },
          beforeImages,
          afterImages,
          medicalPdfs
        );
        toast.success("Documents updated successfully");
      } else {
        await DocumentsService.create(
          { treatmentId },
          beforeImages,
          afterImages,
          medicalPdfs
        );
        toast.success("Documents created successfully");
      }
      navigate("/documents");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          "Failed to save documents. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getPatientLabel = (p: { id: string; name: string }) => p.name || p.id;

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {(loading || patientsLoading) && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {id ? "Update Documents" : "Upload Documents"}
                </h2>
                <button
                  className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              <form
                noValidate
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {/* Patient */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Patient <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedPatientId}
                    onChange={(e) => setSelectedPatientId(e.target.value)}
                    disabled={patientsLoading}
                    className="mt-1 w-full p-2 border rounded-lg"
                  >
                    {patientsLoading ? (
                      <option value="">Loading patients...</option>
                    ) : (
                      <>
                        <option value="">Select Patient</option>
                        {patients.map((p) => (
                          <option key={p.id} value={p.id}>
                            {getPatientLabel(p)}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {/* Medical PDFs */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Medical Documents
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFilesChange(setMedicalPdfs)}
                    className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
                  />
                  {medicalPdfs.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">Selected: 1</p>
                  )}
                </div> */}

                {/* Before Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Before Images
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFilesChange(setBeforeImages)}
                    className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
                  />
                  {beforeImages.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">Selected: 1</p>
                  )}
                </div>

                {/* After Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    After Images
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFilesChange(setAfterImages)}
                    className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
                  />
                  {afterImages.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">Selected: 1</p>
                  )}
                </div>

                <div className="md:col-span-2 flex justify-between items-center mt-4">
                  <button
                    type="button"
                    disabled={
                      beforeImages.length === 0 &&
                      afterImages.length === 0 &&
                      medicalPdfs.length === 0
                    }
                    onClick={() => setIsPreviewOpen(true)}
                    className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition disabled:opacity-60 text-sm"
                  >
                    View Uploaded Items
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                      onClick={() => window.history.back()}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-5 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-60"
                    >
                      {loading
                        ? "Saving..."
                        : id
                        ? "Update Documents"
                        : "Upload Documents"}
                    </button>
                  </div>
                </div>
              </form>

              {/* Preview Modal */}
              {isPreviewOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 max-h-[80vh] overflow-y-auto"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Uploaded Items
                      </h3>
                      <button
                        type="button"
                        onClick={() => setIsPreviewOpen(false)}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    {/* Before Images Preview */}
                    {beforeImages.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          Before Images ({beforeImages.length})
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {beforeImages.map((file, index) => (
                            <div
                              key={`${file.name}-${index}`}
                              className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="w-full h-32 object-cover"
                              />
                              <div className="px-2 py-1 text-[11px] text-gray-600 truncate">
                                {file.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* After Images Preview */}
                    {afterImages.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          After Images ({afterImages.length})
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {afterImages.map((file, index) => (
                            <div
                              key={`${file.name}-${index}`}
                              className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="w-full h-32 object-cover"
                              />
                              <div className="px-2 py-1 text-[11px] text-gray-600 truncate">
                                {file.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* PDF Preview */}
                    {/* {medicalPdfs.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          Medical Documents (PDF) ({medicalPdfs.length})
                        </h4>
                        <ul className="text-xs text-gray-700 space-y-1">
                          {medicalPdfs.map((file, index) => (
                            <li
                              key={`${file.name}-${index}`}
                              className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50"
                            >
                              <span className="truncate max-w-xs">
                                {file.name}
                              </span>
                              <span className="ml-2 text-[10px] text-gray-500">
                                PDF
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )} */}
                  </motion.div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default DocumentsForm;


